import { useEffect, MutableRefObject } from 'react';

/**
 * Hook that triggers a callback when a click occurs outside of the specified element.
 * @description - this is usually used for popovers or dialogs where the element should be closed
 * when the user clicks outside of it (to prevent having multiple open at the same time).
 *
 * The hook adds a mousedown event listener to the document and checks if the click target is outside of the specified ref element.
 * If it is, and allowExecution is true, it executes the provided action callback where you can update the state of your component to close the popover/dialog.
 *
 * @param ref - The ref of the element to detect outside clicks for.
 * @param action - The callback function to execute when an outside click is detected.
 * @param allowExecution - Whether to allow the execution of the action. Defaults to true.
 */
export const useClickOutside = ({
  ref,
  action,
  allowExecution = true,
}: {
  ref: MutableRefObject<HTMLElement>;
  action: (event: MouseEvent) => void;
  allowExecution?: boolean;
}): void => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (typeof action !== 'function' || !ref?.current) {
        return;
      }

      // only fire the action if the the target of the click action
      // is not within the ref element and allowExecution is true
      if (allowExecution && !ref?.current?.contains(event.target as Node)) {
        action(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [action, allowExecution, ref]);
};
